/* Progressive controls delegate to the product's existing variant and physical viewers. */
(() => {
  function init(root) {
    if (!root || root.dataset.editorialReady) return;
    root.dataset.editorialReady = 'true';
    const form = root.querySelector('#sp-form');
    const selects = root.querySelector('[data-spe-selects]');
    const groups = [...(form?.querySelectorAll('.spec-opt-group') || [])].filter(g => g.querySelector('input[type="radio"]')).sort((a, b) => Number(b.hasAttribute('data-pt')) - Number(a.hasAttribute('data-pt')));
    const bindings = [];
    let product = { variants: [] };
    try { product = JSON.parse(document.getElementById('sp-pjson')?.textContent || '{}'); } catch (_) { /* Original controls remain available. */ }
    function updatePlace(id) {
      const scene = root.querySelector('[data-spe-place-scene]');
      const object = root.querySelector('[data-spe-place-art]');
      const pending = root.querySelector('[data-spe-place-pending]');
      const readout = root.querySelector('[data-spe-place-readout]');
      if (!scene || !object) return;
      const variant = (product.variants || []).find(v => String(v.id) === String(id) && v.available);
      const dimensions = variant && window.CADimensions?.parse(variant.title);
      object.hidden = !dimensions;
      if (pending) pending.hidden = !!dimensions;
      if (!dimensions || !window.CASpatialGeometry) {
        if (readout) readout.textContent = 'Choose a format and size to compare its print area.';
        scene.dataset.overflow = 'false';
        return;
      }
      const format = /acrylic/i.test(variant.title) ? 'acrylic' : /fram/i.test(variant.title) ? 'framed' : /canvas/i.test(variant.title) ? 'canvas' : 'poster';
      const width = scene.clientWidth, height = scene.clientHeight;
      const p = window.CASpatialGeometry.project({ ...dimensions, format },
        { referenceCm: 366, referenceFraction: 1, axis: 'width', anchorX: .5, anchorY: (244 - 150) / 244 },
        { left: width * 8 / 250, top: height * 6 / 180, width: width * 234 / 250, height: height * 156 / 180 });
      if (!p) return;
      object.dataset.format = format;
      object.style.cssText = 'left:' + p.left + 'px;top:' + p.top + 'px;width:' + p.printWidth + 'px;height:' + p.printHeight + 'px;border-width:' + p.framePx + 'px';
      scene.dataset.overflow = String(p.exceedsScene);
      if (readout) readout.textContent = dimensions.widthCm.toFixed(1).replace(/\.0$/, '') + ' × ' + dimensions.heightCm.toFixed(1).replace(/\.0$/, '') + ' cm print area. ' + (p.exceedsScene ? 'This size extends beyond the illustrated wall. ' : '') + (format === 'framed' ? 'Frame outer dimensions are unverified.' : 'Approximate placement.');
    }
    groups.forEach((group, index) => {
      const label = document.createElement('label');
      label.className = 'spe-select';
      const caption = document.createElement('span');
      caption.textContent = group.querySelector('.spec-opt-label')?.textContent.trim() || 'Option';
      const select = document.createElement('select');
      select.id = 'spe-select-' + index;
      select.setAttribute('aria-label', caption.textContent);
      const prompt = new Option(group.hasAttribute('data-sz') ? 'Choose a size' : 'Choose a format', '');
      prompt.disabled = true;
      select.add(prompt);
      [...group.querySelectorAll('input[type="radio"]')].forEach(input => {
        const sourceLabel = root.querySelector('label[for="' + CSS.escape(input.id) + '"]');
        select.add(new Option(input.dataset.displayLabel || sourceLabel?.querySelector('.spec-opt-title')?.textContent.trim() || input.value, input.value));
      });
      select.value = group.querySelector('input:checked')?.value || '';
      select.addEventListener('change', () => {
        const input = [...group.querySelectorAll('input[type="radio"]')].find(r => r.value === select.value && !r.disabled);
        if (!input) return;
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        refresh();
      });
      label.append(caption, select);
      selects?.append(label);
      bindings.push({ group, select });
    });
    if (bindings.length && selects) root.classList.add('spe-proxy-ready');
    let queued = false;
    function refresh() {
      bindings.forEach(({ group, select }) => {
        select.disabled = group.hidden;
        [...select.options].slice(1).forEach(option => {
          const input = [...group.querySelectorAll('input[type="radio"]')].find(r => r.value === option.value);
          option.disabled = !input || input.disabled;
          option.hidden = !input || input.disabled;
        });
        select.value = group.querySelector('input:checked:not(:disabled)')?.value || '';
      });
      const format = root.querySelector('[data-pt] input:checked');
      const note = format && root.querySelector('label[for="' + CSS.escape(format.id) + '"] .spec-format-detail');
      const dest = root.querySelector('[data-spe-format-note]');
      if (dest && note?.textContent.trim()) dest.textContent = note.textContent.trim();
      const id = root.querySelector('#sp-varid')?.value || '';
      if (id !== lastQrId) updateQr(id);
      updatePlace(id);
    }
    let lastQrId = null;
    function updateQr(id) {
      const box = root.querySelector('[data-spe-qr]');
      if (!box || typeof window.qrcode !== 'function') return;
      const link = new URL(location.href);
      link.hash = 'see-on-wall';
      if (id) link.searchParams.set('variant', id); else link.searchParams.delete('variant');
      const qr = window.qrcode(0, 'M');
      qr.addData(link.href); qr.make();
      box.innerHTML = qr.createSvgTag({ cellSize: 3, margin: 0, scalable: true });
      lastQrId = id;
    }
    function queueRefresh() {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => { queued = false; refresh(); });
    }
    const observer = new MutationObserver(queueRefresh);
    groups.forEach(group => observer.observe(group, { subtree: true, attributes: true, attributeFilter: ['disabled', 'hidden', 'class'] }));
    form?.addEventListener('change', queueRefresh);
    document.addEventListener('ca:variant-resolved', queueRefresh);
    const placeScene = root.querySelector('[data-spe-place-scene]');
    if (placeScene && window.ResizeObserver) new ResizeObserver(queueRefresh).observe(placeScene);
    // Relocate the existing room launcher; its selected-size validation remains authoritative.
    const roomButton = root.querySelector('#sp-scale-open');
    const roomDest = root.querySelector('[data-spe-room-button]');
    if (roomButton && roomDest) roomDest.append(roomButton);
    let zoom = 1;
    function applyZoom() {
      root.style.setProperty('--spe-zoom', zoom);
      root.querySelector('[data-spe-zoom="out"]')?.toggleAttribute('disabled', zoom <= 0.8);
      root.querySelector('[data-spe-zoom="in"]')?.toggleAttribute('disabled', zoom >= 1.6);
    }
    root.querySelectorAll('[data-spe-zoom]').forEach(button => button.addEventListener('click', () => {
      zoom = Math.min(1.6, Math.max(0.8, +(zoom + (button.dataset.speZoom === 'in' ? 0.2 : -0.2)).toFixed(1)));
      applyZoom();
    }));
    root.querySelector('[data-spe-reset]')?.addEventListener('click', () => { zoom = 1; applyZoom(); });
    root.querySelectorAll('[data-spe-view]').forEach(button => button.addEventListener('click', () => {
      const launcher = root.querySelector('[data-ca-reality-open]');
      if (!launcher || launcher.hidden || launcher.disabled) return;
      launcher.click();
      const modalId = root.querySelector('[data-ca-reality]')?.dataset.sectionId;
      const modal = modalId ? document.querySelector('[data-ca-reality-modal="' + CSS.escape(modalId) + '"]') : document.querySelector('[data-ca-reality-modal]');
      const applyPreset = () => {
        if (modal && !modal.hidden) modal.querySelector('[data-ca-reality-preset="' + button.dataset.speView + '"]')?.click();
      };
      // The first launch can load WebGL asynchronously. Apply the requested view after it is ready.
      if (root.querySelector('.ca-reality.is-ready')) requestAnimationFrame(applyPreset);
      else document.addEventListener('reality_viewer_ready', applyPreset, { once: true });
    }));
    const launcher = root.querySelector('[data-ca-reality-open]');
    const syncViewer = () => root.querySelectorAll('[data-spe-view]').forEach(b => { b.disabled = !launcher || launcher.hidden || launcher.disabled; });
    if (launcher) new MutationObserver(syncViewer).observe(launcher, { attributes: true, attributeFilter: ['hidden', 'disabled'] });
    syncViewer(); applyZoom(); refresh();
    window.addEventListener('load', refresh, { once: true });
  }
  const boot = () => document.querySelectorAll('.spec--editorial').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', boot);
})();
